import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

export type AccessDeniedReason = 'permissions' | 'past_due' | null;

interface RouteGuardResult {
  isAccessDenied: boolean;
  accessDeniedReason: AccessDeniedReason;
  closeModal: () => void;
}

export const useRouteGuard = (protectedPaths: string[] = ['/create', '/edit']): RouteGuardResult => {
  const { user, isLoggedIn } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [accessDeniedReason, setAccessDeniedReason] = useState<AccessDeniedReason>(null);

  useEffect(() => {
    // Only check if user is logged in and we're on a protected path
    if (!isLoggedIn || !user) return;

    const currentPath = location.pathname;
    const isProtectedPath = protectedPaths.some(path => currentPath.startsWith(path));
    
    if (!isProtectedPath) return;

    // Check if subscription is past due
    if (user.subscription_status === 'past_due') {
      setAccessDeniedReason('past_due');
      return;
    }

    // Check if user has create permissions
    if (!user.user_permissions?.can_create) {
      setAccessDeniedReason('permissions');
      return;
    }

    // User has access, clear any existing denial reason
    setAccessDeniedReason(null);
  }, [user, isLoggedIn, location.pathname, protectedPaths]);

  const closeModal = () => {
    setAccessDeniedReason(null);
    // Navigate away from protected route
    navigate('/', { replace: true });
  };

  return {
    isAccessDenied: accessDeniedReason !== null,
    accessDeniedReason,
    closeModal
  };
};

export default useRouteGuard;